import { prefersReducedMotion } from '@theme/utilities';
import { getScrollContainer } from '@theme/scroll-container';

/**
 * Was 1400 — reported as reading like the page had hung rather than as a
 * considered transition, especially over long distances (e.g. to FAQ near
 * the bottom). Still eased, just fast enough not to feel broken.
 */
const SCROLL_DURATION = 600;

/**
 * ease-out, not ease-in-out: ease-in-out's flat start (near-zero velocity
 * for its first ~15% of duration) read as a delay before the scroll
 * responded at all. ease-out moves immediately from the first frame and
 * decelerates into the landing instead.
 *
 * @param {number} t
 * @returns {number}
 */
function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * @returns {number}
 */
function getHeaderOffset() {
  const value = getComputedStyle(document.body).getPropertyValue('--header-height');
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * @param {HTMLElement} container
 * @param {number} targetTop
 */
function animateScrollTo(container, targetTop) {
  const startTop = container.scrollTop;
  const distance = targetTop - startTop;

  if (Math.abs(distance) < 1) return;

  const startTime = performance.now();

  /** @param {number} now */
  function step(now) {
    const progress = Math.min((now - startTime) / SCROLL_DURATION, 1);
    // behavior: 'instant' is required, not cosmetic — html/.page-wrapper both set
    // scroll-behavior: smooth (assets/base.css), and a plain `scrollTop =` assignment
    // still routes through that CSS on current browsers, so every frame here would
    // otherwise kick off its own competing native smooth-scroll on top of this
    // easing, fighting itself and reading as laggy/delayed. Same override
    // scroll-container.js already uses for the same reason (restoreSavedScrollTop).
    container.scrollTo({ top: startTop + distance * easeOutCubic(progress), behavior: 'instant' });

    if (progress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

/**
 * Closes the mobile header drawer so the target section is visible once the
 * scroll happens. Goes through the <header-drawer> custom element's own
 * close() (assets/header-drawer.js) rather than clearing the <details>
 * element's `open` attribute directly — that attribute is only half of the
 * drawer's state: close() also removes the `.menu-open` class driving the
 * CSS transition/icon swap, resets `aria-expanded`, and releases the focus
 * trap set up on open(). Removing just the attribute left `.menu-open`
 * stuck applied (close icon never reverting to the hamburger) and the focus
 * trap active on a now-hidden subtree, which read as the header disappearing
 * and the page freezing on the next interaction.
 * @param {Element} link
 */
function closeMobileDrawer(link) {
  const drawer = link.closest('header-drawer');
  if (drawer && typeof (/** @type {any} */ (drawer).close) === 'function') {
    /** @type {any} */ (drawer).close();
  }
}

/** @param {MouseEvent} event */
function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a[href*="#"]');
  if (!link || !(link instanceof HTMLAnchorElement)) return;
  if (link.classList.contains('skip-to-content-link')) return;

  const url = new URL(link.href, window.location.href);

  if (url.pathname !== window.location.pathname || url.search !== window.location.search) return;
  if (!url.hash || url.hash === '#') return;

  /** @type {HTMLElement | null} */
  let target;
  try {
    target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  } catch {
    return;
  }
  if (!target) return;

  event.preventDefault();
  closeMobileDrawer(link);

  const container = /** @type {HTMLElement} */ (getScrollContainer());
  // On mobile the root element is the scroll container, and its rect.top already
  // moves with scrolling (-scrollTop), so subtracting it would count the scroll twice.
  const isRoot = container === document.scrollingElement || container === document.documentElement;
  const containerTop = isRoot ? 0 : container.getBoundingClientRect().top;
  const targetRect = target.getBoundingClientRect();
  const targetTop = container.scrollTop + (targetRect.top - containerTop) - getHeaderOffset();

  if (prefersReducedMotion()) {
    container.scrollTo({ top: targetTop, behavior: 'instant' });
  } else {
    animateScrollTo(container, targetTop);
  }

  history.pushState(null, '', url.hash);

  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
    target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
  }
  target.focus({ preventScroll: true });
}

document.addEventListener('click', handleClick);
